<template>
    <CompassModal
        v-model:visible="showModal"
        size="small"
        class="popup-mailchimp-settings"
        @closed="closePopup"
    >
        <template #header>
            <div class="dialog__header">
                <div class="dialog__header--title">
                    <SaasIcon icon="mailChimp" width="56" height="56" />
                    <div class="mt-3"><span class="bold fs-5">Mailchimp</span></div>
                </div>
                <div class="dialog__header--close">
                    <CompassIconButton icon="close" @click="closePopup" />
                </div>
            </div>
        </template>
        <template #default>
            <div class="dialog__default">
                <el-form
                    ref="mailchimpForm"
                    :model="mailchimpConfig"
                    :rules="rules"
                    labelPosition="top"
                >
                    <span>{{ $l10n.__('Connectez votre compte Mailchimp sur Sellsy.') }}</span>

                    <el-divider></el-divider>

                    <div v-if="error" class="alert alert-danger mb-3">{{ error }}</div>

                    <span class="semibold mb-1 fs-4">{{ $l10n.__('Installation') }}</span>

                    <div class="mb-3">
                        <span>
                            {{
                                $l10n.__(
                                    'Connectez-vous à votre compte Mailchimp pour récupérer votre clé API-Account / Extra / API Keys - et entrez la ci-dessous.',
                                )
                            }}
                        </span>
                        <span>
                            {{ $l10n.__('Pour en savoir plus vous pouvez consulter cette ') }}

                            <a
                                class="semibold pointer hover-primary-100"
                                target="_blank"
                                href="https://help.sellsy.com/hc/connecter-sellsy-avec-ses-outils/mailchimp"
                                >{{ $l10n.__('documentation') }}</a
                            >.
                        </span>
                    </div>

                    <el-form-item prop="apikey" :error="errors.get('apikey')">
                        <template #label>
                            <span class="semibold mb-1 gray-100">
                                {{ $l10n.__("Clé d'API") }}
                            </span>
                        </template>
                        <el-input
                            v-model="mailchimpConfig.apikey"
                            placeholder="Renseigner"
                        ></el-input>
                    </el-form-item>

                    <div
                        v-if="
                            isConnected &&
                            (mode === 'marketing' || mailchimpConfig.marketing.loaded) &&
                            !apiKeyChanged
                        "
                        class="flex-column marketing mt-3"
                    >
                        <span class="semibold fs-4 mb-1">
                            {{ $l10n.__('Audience pour les campagnes emailing') }}
                        </span>
                        <span class="mb-3">
                            {{
                                $l10n.__(
                                    'Vous pouvez sélectionner l’audience par défaut pour vos campagnes emailing.',
                                )
                            }}
                        </span>
                        <el-form-item prop="marketing">
                            <template #label>
                                <span class="semibold mb-1 gray-100">
                                    {{ $l10n.__('Liste par défaut') }}
                                </span>
                            </template>
                            <SelectInput
                                v-model="mailchimpConfig.marketing.audience_id"
                                :appendToBody="true"
                                mode="edit"
                                labelColumn="name"
                                idColumn="id"
                                :selectNothing="$t.__('common.labels.none')"
                                :options="mailchimpConfig.audiences"
                                :placeholder="$t.__('common.actions.select_default_list')"
                            />
                        </el-form-item>
                    </div>

                    <div v-if="mode === 'settings'" class="flex-column synchro-contact">
                        <span class="semibold fs-4 mb-1">
                            {{ $l10n.__('Synchroniser les contacts') }}
                        </span>

                        <div v-if="isConnected && !apiKeyChanged" class="flex-column">
                            <span class="mb-3">
                                {{
                                    $l10n.__(
                                        'Vous pouvez synchroniser vos contacts Sellsy sur votre compte Mailchimp.',
                                    )
                                }}
                            </span>

                            <el-form-item prop="mcp_defaultList">
                                <template #label>
                                    <span class="semibold mb-1 gray-100">
                                        {{ $l10n.__('Liste par défaut') }}
                                    </span>
                                </template>
                                <SelectInput
                                    v-model="mailchimpConfig.contacts_synchronization.audience_id"
                                    mode="edit"
                                    labelColumn="name"
                                    :appendToBody="true"
                                    idColumn="id"
                                    :selectNothing="$t.__('common.labels.none')"
                                    :options="mailchimpConfig.audiences"
                                    :placeholder="$t.__('common.actions.select_default_list')"
                                >
                                </SelectInput>
                            </el-form-item>

                            <div v-if="!audienceIdContactChanged">
                                <span class="semibold mb-1 gray-100">
                                    {{ $l10n.__('Valeurs par défaut') }}
                                </span>

                                <el-form-item
                                    v-for="(mergeField, index) in mailchimpConfig
                                        .contacts_synchronization.merge_fields"
                                    :key="index"
                                >
                                    <SelectInput
                                        v-if="mergeField.type === 'dropdown'"
                                        v-model="mergeField.default_value"
                                        mode="edit"
                                        labelColumn="label"
                                        idColumn="label"
                                        :options="mergeField.choices"
                                        :appendToBody="true"
                                        :placeholder="mergeField.tag"
                                    />
                                    <el-input
                                        v-else
                                        v-model="mergeField.default_value"
                                        :placeholder="mergeField.tag"
                                    ></el-input>
                                </el-form-item>
                            </div>
                            <div>
                                <CompassButton
                                    :label="
                                        $t.__('popups.PopupMailchimpSettings.synchronize_contact')
                                    "
                                    type="tertiary"
                                    class="mb-2"
                                    @click="onSyncroContact"
                                />
                            </div>
                        </div>
                        <span class="mb-3">
                            {{
                                $l10n.__(
                                    'L’action suivante va vous permettre d’activer la synchronisation Mailchimp pour les contacts de vos clients/prospects/fournisseurs en un seul clic.',
                                )
                            }}
                        </span>

                        <el-form-item prop="syncroEnabled">
                            <RadioInput
                                v-model="mailchimpConfig.contacts_synchronization.enabled"
                                mode="edit"
                                :choices="choicesSyncro"
                            />
                        </el-form-item>

                        <el-form-item prop="mcp_syncroTypeContact">
                            <SelectInput
                                v-model="mailchimpConfig.contacts_synchronization.filter"
                                mode="edit"
                                :appendToBody="true"
                                :options="filter"
                            />
                        </el-form-item>

                        <div>
                            <CompassButton
                                :label="$t.__('popups.PopupMailchimpSettings.update_contact')"
                                type="tertiary"
                                @click="onUpdateContact"
                            />
                        </div>
                    </div>
                </el-form>
            </div>
        </template>
        <template #footer>
            <div class="dialog__footer">
                <CompassButton
                    :label="$t.__('common.actions.cancel')"
                    type="secondary"
                    @click="closePopup"
                />
                <CompassButton
                    :label="$t.__('common.actions.save')"
                    type="primary"
                    :loading="isFormSubmitted"
                    class="ml-2"
                    @click="onSubmitForm"
                />
            </div>
            <preloader :display="loading" />
        </template>
    </CompassModal>
</template>

<script>
import SelectInput from 'vcomponents/inputs/SelectInput.vue';
import RadioInput from 'vcomponents/inputs/RadioInput.vue';
import FormMixins from '../../mixins/FormMixins';
import MailChimpService from 'api/providerMarketing/MailChimpService';
import Preloader from 'vcomponents/utilities/Preloader.vue';
import SaasIcon from 'vcomponents/common/SaasIcon.vue';

export default {
    components: {
        SaasIcon,
        SelectInput,
        RadioInput,
        Preloader,
    },
    mixins: [FormMixins],
    props: {
        mode: {
            type: String,
            required: true,
            default: 'settings',
        },
        callback: {
            type: String,
            required: false,
            default: '',
        },
    },
    data() {
        return {
            showModal: true,
            optionsContactSync: ['all', 'clients', 'prospects', 'suppliers'],
            mailchimpConfig: {
                apikey: '',
                contacts_synchronization: {
                    enabled: true,
                    filter: 'all',
                },
            },
            choicesSyncro: [
                {
                    label: this.$l10n.__('Activer la synchronisation'),
                    value: true,
                },
                {
                    label: this.$l10n.__('Désactiver la synchronisation'),
                    value: false,
                },
            ],
            rules: {
                apikey: [
                    {
                        required: true,
                        message: this.$l10n.__("Merci de renseigner une clé d'API."),
                        trigger: 'blur',
                    },
                    {
                        max: 64,
                        message: this.$l10n.__("La clé d'API est trop longue"),
                        trigger: 'blur',
                    },
                ],
            },
            filter: [
                {
                    label: this.$l10n.__('Toutes les sociétés'),
                    value: 'all',
                },
                {
                    label: this.$l10n.__('Client'),
                    value: 'clients',
                },
                {
                    label: this.$l10n.__('Prospect'),
                    value: 'prospects',
                },
                {
                    label: this.$l10n.__('Fournisseur'),
                    value: 'suppliers',
                },
            ],
            filterSelected: 'all',
            loading: false,
            isConnected: false,
            error: null,
            oldApiKey: '',
            oldAudienceIdContactSync: '',
            isFormSubmitted: false,
        };
    },
    computed: {
        audienceIdContactChanged() {
            return (
                this.mailchimpConfig.contacts_synchronization.audience_id !==
                this.oldAudienceIdContactSync
            );
        },
        apiKeyChanged() {
            this.error = null;
            return this.mailchimpConfig.apikey !== this.oldApiKey;
        },
    },
    created() {
        this.toggleLoading();
        MailChimpService.getConfig()
            .then((response) => {
                this.mailchimpConfig = response.data;
                this.isConnected = response.data.apikey !== '';
                this.oldApiKey = _.cloneDeep(this.mailchimpConfig.apikey);
                this.oldAudienceIdContactSync = _.cloneDeep(
                    this.mailchimpConfig.contacts_synchronization.audience_id,
                );
                this.toggleLoading();
                this.error = null;
            })
            .catch((error) => {
                this.toggleLoading();
                if (error.response.data.error.code !== 404) {
                    this.onError(error.response.data.error);
                }
            });
    },
    methods: {
        onSubmitForm() {
            this.error = null;
            this.submitForm('mailchimpForm');
        },
        closePopup() {
            this.$emit('close');
        },
        toggleLoading() {
            this.loading = !this.loading;
        },
        // eslint-disable-next-line vue/no-unused-properties
        save() {
            this.isFormSubmitted = true;
            if (this.isConnected) {
                if (this.audienceIdContactChanged) {
                    this.mailchimpConfig.contacts_synchronization.merge_fields = [];
                }
                if (this.apiKeyChanged) {
                    this.mailchimpConfig.contacts_synchronization.merge_fields = [];
                    this.mailchimpConfig.contacts_synchronization.audience_id = null;
                    this.mailchimpConfig.marketing.audience_id = null;
                }
                MailChimpService.updateConfig(this.mailchimpConfig)
                    .then((response) => {
                        if (this.mode === 'marketing' && this.oldApiKey === response.data.apikey) {
                            // Mailchimp provider ID => 3, we trigger callback to update marketing provider selected
                            EventBus.emit(this.callback, 3);
                        } else {
                            Push.success(this.$l10n.__('Modifications bien prises en compte.'));
                        }
                        if (this.oldApiKey !== response.data.apikey) {
                            this.mailchimpConfig = response.data;
                            this.oldApiKey = _.cloneDeep(this.mailchimpConfig.apikey);
                            this.oldAudienceIdContactSync = _.cloneDeep(
                                this.mailchimpConfig.contacts_synchronization.audience_id,
                            );
                            this.isConnected = response.data.apikey !== '';
                        } else {
                            this.closePopup();
                        }
                    })
                    .catch((error) => {
                        this.onError(error.response.data.error);
                    })
                    .finally(() => (this.isFormSubmitted = false));
            } else {
                MailChimpService.saveConfig(this.mailchimpConfig)
                    .then((response) => {
                        if (this.mode === 'marketing') {
                            // Mailchimp provider ID => 3, we trigger callback to update marketing provider selected
                            EventBus.emit(this.callback, 3);
                        } else {
                            Push.success(this.$l10n.__('Modifications bien prises en compte.'));
                        }
                        this.mailchimpConfig = response.data;
                        this.oldApiKey = _.cloneDeep(this.mailchimpConfig.apikey);
                        this.oldAudienceIdContactSync = _.cloneDeep(
                            this.mailchimpConfig.contacts_synchronization.audience_id,
                        );
                        this.isConnected = response.data.apikey !== '';
                    })
                    .catch((error) => {
                        this.onError(error.response.data.error);
                    })
                    .finally(() => (this.isFormSubmitted = false));
            }
        },
        onError(error) {
            if (error.code === 400 && error.details.apikey) {
                this.errors.setOneError('apikey', error.details.apikey);
            } else {
                this.error = error.message;
            }
        },
        onSyncroContact() {
            this.$ajax.post(
                '?_f=mailchimp_sync&action=allContacts',
                null,
                null,
                (response) => Push.success(response.response),
                () =>
                    Push.error(
                        this.$l10n.__('Une erreur est survenue, merci de rééssayer ultérieurement'),
                    ),
            );
        },
        onUpdateContact() {
            const payload = {
                mcontactyes_active: this.mailchimpConfig.contacts_synchronization.enabled,
                mcontactyes_thirdtype: this.mailchimpConfig.contacts_synchronization.filter,
            };
            this.$ajax.post(
                '?_f=mailchimp_prefs&action=contactMCYes',
                null,
                payload,
                () => {
                    Push.success(this.$l10n.__('Vos contacts ont bien été mis à jour.'));
                },
                () => {
                    Push.error(
                        this.$l10n.__('Une erreur est survenue, merci de rééssayer ultérieurement'),
                    );
                },
            );
        },
    },
};
</script>

<style lang="scss">
@use '~@sellsy/styleguide/src/_variables' as *;

.popup-mailchimp-settings {
    span {
        line-height: 20px;
    }

    .dialog {
        &__header {
            display: flex;
            justify-content: space-between;
            padding: 24px 32px;

            &--title {
                display: flex;
                flex-direction: column;
            }

            &--close {
                display: flex;
                justify-content: flex-end;
                align-items: flex-start;
            }
        }

        &__default {
            padding-bottom: 24px;
        }

        &__footer {
            display: flex;
            justify-content: flex-end;
            padding: 0 32px 24px 32px;
        }
    }

    .el-form {
        .el-form-item {
            .el-form-item__label {
                margin-bottom: 4px;
                line-height: 20px;
            }

            .el-form-item__content {
                .el-radio-group .el-radio {
                    margin-right: 16px;
                    color: $gray-100;

                    .el-radio__input > input {
                        margin: 0;
                    }
                    .el-radio__label {
                        vertical-align: middle;
                    }
                }

                .el-radio-group .el-radio:last-child {
                    margin-right: 0;
                }

                .el-select .el-input.el-input--suffix .el-input__suffix {
                    right: 8px;
                }
            }
        }
    }

    .btn-update-contact {
        margin-bottom: 10px;
    }
}
</style>
